import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Send, Package, Globe, ChevronRight, Clock, CheckCircle, XCircle,
  DollarSign, Search, Filter, Loader2, Building2, RefreshCw, Eye
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const STATUS_CFG = {
  pending: { color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300", icon: Clock },
  approved: { color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", icon: CheckCircle },
  completed: { color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300", icon: CheckCircle },
  rejected: { color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300", icon: XCircle },
};

const ServiceRequestsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [actionStatus, setActionStatus] = useState("");
  const [actionNotes, setActionNotes] = useState("");
  const [actionPrice, setActionPrice] = useState("");
  const [acting, setActing] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (typeFilter !== "all") params.append("type", typeFilter);
      const res = await api.get(`/requests?${params}`);
      setRequests(res.data.requests || []);
      setStatusCounts(res.data.status_counts || {});
    } catch { toast.error("Failed to load requests"); }
    finally { setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Auto-select request from ?review= query param
  useEffect(() => {
    const reviewId = searchParams.get("review");
    if (reviewId && requests.length > 0 && !selected) {
      const target = requests.find(r => r.id === reviewId);
      if (target) {
        setSelected(target);
        setActionNotes(target.admin_notes || "");
        setActionPrice(target.price?.toString() || "");
        searchParams.delete("review");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [requests, searchParams, selected, setSearchParams]);

  const handleAction = async () => {
    if (!selected || !actionStatus) return;
    setActing(true);
    try {
      await api.put(`/requests/${selected.id}`, {
        status: actionStatus,
        admin_notes: actionNotes,
        price: parseFloat(actionPrice) || 0,
      });
      toast.success(`Request ${actionStatus}`);
      setSelected(null);
      setActionStatus("");
      setActionNotes("");
      setActionPrice("");
      fetchRequests();
    } catch { toast.error("Action failed"); }
    finally { setActing(false); }
  };

  const totalPending = statusCounts.pending || 0;

  return (
    <AdminLayout title="Service Requests">
      <div className="space-y-5" data-testid="service-requests-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" data-testid="service-requests-title">Service Requests</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage custom module requests and embed engine access from tenants
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Status cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["pending", "approved", "completed", "rejected"].map((s) => {
            const cfg = STATUS_CFG[s];
            const Icon = cfg.icon;
            return (
              <Card key={s} className={cn("cursor-pointer transition-all hover:ring-1", statusFilter === s && "ring-2 ring-blue-500")}
                onClick={() => setStatusFilter(statusFilter === s ? "all" : s)} data-testid={`status-card-${s}`}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", cfg.color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold leading-none">{statusCounts[s] || 0}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">{s}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[200px] h-9" data-testid="type-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="custom_module">Custom Modules</SelectItem>
              <SelectItem value="embed_engine">Embed Engine</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requests list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16">
            <Send className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No service requests yet.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((req) => {
              const cfg = STATUS_CFG[req.status] || STATUS_CFG.pending;
              const isModule = req.type === "custom_module";
              return (
                <button key={req.id} onClick={() => { setSelected(req); setActionStatus(""); setActionNotes(req.admin_notes || ""); setActionPrice(req.price?.toString() || ""); }}
                  className="w-full text-left p-4 rounded-lg border bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-start gap-4"
                  data-testid={`request-${req.id}`}>
                  <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                    isModule ? "bg-violet-100 dark:bg-violet-900/30" : "bg-blue-100 dark:bg-blue-900/30")}>
                    {isModule ? <Package className="w-5 h-5 text-violet-600 dark:text-violet-400" /> : <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{isModule ? "Custom Module" : "Embed Engine Access"}</span>
                      <Badge className={cn("text-[10px]", cfg.color)}>{req.status}</Badge>
                      {req.price > 0 && <Badge variant="outline" className="text-[10px] gap-1"><DollarSign className="w-3 h-3" />{req.price}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{req.description || req.use_case}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{req.tenant_name || req.tenant_id}</span>
                      <span>{req.requested_by}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(req.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}

        {/* Detail/Action Dialog */}
        <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selected?.type === "custom_module" ? <Package className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
                {selected?.type === "custom_module" ? "Custom Module Request" : "Embed Engine Request"}
              </DialogTitle>
            </DialogHeader>
            {selected && (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs font-medium text-muted-foreground mb-0.5">Tenant</p><p>{selected.tenant_name || selected.tenant_id}</p></div>
                  <div><p className="text-xs font-medium text-muted-foreground mb-0.5">Requested By</p><p>{selected.requested_by_name || selected.requested_by}</p></div>
                  {selected.course_title && <div className="col-span-2"><p className="text-xs font-medium text-muted-foreground mb-0.5">Course</p><p>{selected.course_title}</p></div>}
                  <div className="col-span-2"><p className="text-xs font-medium text-muted-foreground mb-0.5">Description</p><p className="text-muted-foreground">{selected.description || selected.use_case}</p></div>
                </div>

                {selected.status === "pending" && (
                  <div className="space-y-3 pt-3 border-t">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Take Action</p>
                    <div className="flex gap-2">
                      {["approved", "rejected", "completed"].map((s) => (
                        <Button key={s}
                          variant={actionStatus === s ? (s === "approved" ? "success" : s === "rejected" ? "destructive" : "default") : "outline"}
                          size="sm"
                          onClick={() => setActionStatus(s)} data-testid={`action-${s}`}>
                          {s === "approved" ? "Approve" : s === "rejected" ? "Reject" : "Complete"}
                        </Button>
                      ))}
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Price (if billable, USD)</label>
                      <Input type="number" value={actionPrice} onChange={(e) => setActionPrice(e.target.value)} placeholder="0.00" className="h-9" data-testid="action-price" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Admin Notes</label>
                      <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} rows={2} placeholder="Notes for the tenant..." data-testid="action-notes" />
                    </div>
                  </div>
                )}

                {selected.invoice_id && (
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 text-xs">
                    <span className="font-medium">Invoice generated:</span> {selected.invoice_id}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
              {selected?.status === "pending" && actionStatus && (
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleAction} disabled={acting} data-testid="submit-action">
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Confirm {actionStatus}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ServiceRequestsPage;
